import { useEffect, useRef, useState, useCallback } from 'react'
import mermaid from 'mermaid'
import { Eye, Code, Sparkles, FileQuestion, ZoomIn, ZoomOut, Move, Shapes, Loader2 } from 'lucide-react'
import ArtifactStateOverlay from './ArtifactStateOverlay'
import type { AgentDiagram } from '../agui/streamAgentTypes'

interface DiagramViewerProps {
  diagrams: AgentDiagram[]
  agentStatus?: string
  darkMode?: boolean
  isRegenerating?: boolean
  /** Cria um projeto Studio com o diagrama editável (a promessa resolve quando navega). */
  onOpenInStudio?: (diagram: AgentDiagram) => Promise<void>
}

type MermaidDialect = 'flowchart' | 'sequence' | 'class' | 'er' | 'other'

function sanitizeMermaidLine(line: string, dialect: MermaidDialect): string {
  // sequenceDiagram / erDiagram / stateDiagram / classDiagram: não reescrever parênteses
  if (dialect === 'sequence' || dialect === 'er' || dialect === 'class') {
    return line.replace(/\[([^\]]*)\]/g, (_m, content: string) => {
      return '[' + content.replace(/\\n/g, '<br/>').replace(/\n/g, '<br/>') + ']'
    })
  }

  // C4 e tipos não reconhecidos: só normaliza colchetes (Rel/Person usam muitos parênteses)
  if (dialect === 'other') {
    return line.replace(/\[([^\]]*)\]/g, (_m, content: string) => {
      return '[' + content.replace(/\\n/g, '<br/>').replace(/\n/g, '<br/>') + ']'
    })
  }

  // Flowchart: correções ::: e colchetes
  const malformedClassRegex = /^(\s*\w+)(?:\(.*?\))?:::(\w+)(\[[\s\S]*?\]|\([\s\S]*?\))/
  if (malformedClassRegex.test(line)) {
    line = line.replace(malformedClassRegex, '$1$3::$2')
  }

  const malformedParenRegex = /^(\s*\w+):::(\w+)(\([\s\S]*?\))/
  if (malformedParenRegex.test(line)) {
    line = line.replace(malformedParenRegex, '$1$3::$2')
  }

  // 2. Quebras de linha somente dentro de rótulos [...] (não capturar parênteses de funções Mermaid)
  line = line.replace(/\[([^\]]*)\]/g, (_m, content: string) => {
    return '[' + content.replace(/\\n/g, '<br/>').replace(/\n/g, '<br/>') + ']'
  })

  return line
}

function detectDialectFromSource(src: string): MermaidDialect {
  const s = src.trim().toLowerCase()
  if (s.includes('sequencediagram')) return 'sequence'
  if (s.includes('classdiagram')) return 'class'
  if (s.includes('erdiagram')) return 'er'
  if (
    s.includes('flowchart') ||
    s.includes('graph td') ||
    s.includes('graph lr') ||
    s.includes('graph rl') ||
    s.includes('graph bt')
  ) {
    return 'flowchart'
  }
  return 'other'
}

function extractFirstMermaidDiagram(raw: string): string {
  const blocks = raw.split(/```(?:mermaid)?\s*/gi)

  const diagramTypes = [
    'graph TD', 'graph LR', 'graph RL', 'graph BT',
    'flowchart TD', 'flowchart LR', 'flowchart TB', 'flowchart RL', 'flowchart BT',
    'sequenceDiagram', 'classDiagram', 'stateDiagram', 'erDiagram',
    'gantt', 'pie',
    'C4Context', 'C4Container', 'C4Component', 'C4Dynamic', 'C4Deployment',
  ]

  const processBody = (body: string): string => {
    let cleaned = body.replace(/```.*$/gm, '').trim()
    const dialect = detectDialectFromSource(cleaned)
    const isClassDiagram = dialect === 'class'

    if (dialect === 'sequence' || dialect === 'er') {
      return cleaned
    }

    if (!isClassDiagram) {
      cleaned = cleaned
        .split('\n')
        .filter(
          (line) =>
            !line.trim().toLowerCase().startsWith('classdef ') &&
            !/^class\s+\w+\s+\w+;?/.test(line.trim())
        )
        .map((line) => sanitizeMermaidLine(line, dialect))
        .join('\n')
    } else {
      cleaned = cleaned
        .split('\n')
        .map((line) => sanitizeMermaidLine(line, 'class'))
        .join('\n')
    }

    return cleaned.trim()
  }

  for (const block of blocks) {
    const trimmed = block.trim()
    if (!trimmed) continue

    const hasDiagram = diagramTypes.some((dtype) =>
      trimmed.toLowerCase().includes(dtype.toLowerCase())
    )

    if (hasDiagram) {
      let firstDiagramStart = -1
      for (const dtype of diagramTypes) {
        const idx = trimmed.toLowerCase().indexOf(dtype.toLowerCase())
        if (idx !== -1 && (firstDiagramStart === -1 || idx < firstDiagramStart)) {
          firstDiagramStart = idx
        }
      }

      let cleaned = trimmed
      if (firstDiagramStart > 0) {
        cleaned = cleaned.slice(firstDiagramStart)
      }
      return processBody(cleaned)
    }
  }

  let cleaned = raw
    .replace(/^```mermaid\s*/gim, '')
    .replace(/^```\s*$/gm, '')
    .trim()

  let firstDiagramStart = -1
  for (const dtype of diagramTypes) {
    const idx = cleaned.toLowerCase().indexOf(dtype.toLowerCase())
    if (idx !== -1 && (firstDiagramStart === -1 || idx < firstDiagramStart)) {
      firstDiagramStart = idx
    }
  }
  if (firstDiagramStart > 0) {
    cleaned = cleaned.slice(firstDiagramStart)
  }

  return processBody(cleaned)
}

export default function DiagramViewer({
  diagrams,
  darkMode,
  isRegenerating = false,
  onOpenInStudio,
}: DiagramViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [dsl, setDsl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'preview' | 'code'>('preview')
  const [openingStudio, setOpeningStudio] = useState(false)
  const [isGenerated, setIsGenerated] = useState(false)

  // Garante que activeIndex permanece dentro dos limites quando a lista muda
  const safeIndex = diagrams.length > 0 ? Math.min(activeIndex, diagrams.length - 1) : 0
  const currentDiagram = diagrams[safeIndex]
  const currentRaw = currentDiagram?.code || ''
  const hasDiagram = !!currentRaw.trim() && !currentRaw.startsWith('[ERRO]')

  // Zoom & Pan state
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const isDragging = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const translateStart = useRef({ x: 0, y: 0 })

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: darkMode ? 'dark' : 'default',
      securityLevel: 'loose',
    })
  }, [darkMode])

  const sanitizeDsl = useCallback((raw: string) => {
    return extractFirstMermaidDiagram(raw)
  }, [])

  const render = useCallback(() => {
    if (!containerRef.current || !dsl.trim()) return
    mermaid.initialize({
      startOnLoad: false,
      theme: darkMode ? 'dark' : 'default',
      securityLevel: 'loose',
    })
    containerRef.current.innerHTML = ''
    const id = `mermaid-${Math.random().toString(36).slice(2)}`
    const cleanDsl = sanitizeDsl(dsl)
    if (!cleanDsl) {
      setError('Nenhum diagrama válido encontrado no conteúdo gerado.')
      return
    }
    mermaid
      .render(id, cleanDsl)
      .then(({ svg }) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = svg
        }
        setError(null)
        setIsGenerated(true)
        setScale(1)
        setTranslate({ x: 0, y: 0 })
      })
      .catch((err) => {
        setError(err.message || 'Erro ao renderizar diagrama')
      })
  }, [dsl, darkMode, sanitizeDsl])

  useEffect(() => {
    if (mode === 'preview') {
      render()
    }
  }, [mode, render])

  // Atualiza dsl quando a aba ou dados mudam
  useEffect(() => {
    const raw = currentDiagram?.code || ''
    setDsl(raw)
    setMode('preview')
    setError(null)
    setIsGenerated(false)
  }, [safeIndex, diagrams, currentDiagram])

  // Zoom with wheel
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (mode !== 'preview') return
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setScale((s) => Math.max(0.2, Math.min(4, s + delta)))
  }, [mode])

  // Pan with mouse drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode !== 'preview') return
    isDragging.current = true
    dragStart.current = { x: e.clientX, y: e.clientY }
    translateStart.current = { ...translate }
    if (wrapperRef.current) wrapperRef.current.style.cursor = 'grabbing'
  }, [mode, translate])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return
    const dx = e.clientX - dragStart.current.x
    const dy = e.clientY - dragStart.current.y
    setTranslate({
      x: translateStart.current.x + dx,
      y: translateStart.current.y + dy,
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    isDragging.current = false
    if (wrapperRef.current) wrapperRef.current.style.cursor = 'grab'
  }, [])

  const handleZoomIn = () => setScale((s) => Math.min(4, s + 0.2))
  const handleZoomOut = () => setScale((s) => Math.max(0.2, s - 0.2))
  const handleReset = () => { setScale(1); setTranslate({ x: 0, y: 0 }) }

  const tabs = diagrams.map((d, i) => ({
    key: i,
    label: d.name || `Diagrama ${i + 1}`,
  }))

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Diagrama</h3>
          {hasDiagram && isGenerated && !isRegenerating && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
              <Sparkles size={10} />
              Gerado por Van Gogh a partir do SDD
            </span>
          )}
          {/* Diagram tabs (dinâmicas — Van Gogh decide quantos) */}
          {tabs.length > 0 && (
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 max-w-[420px] overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveIndex(tab.key)}
                  className={`shrink-0 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap max-w-[200px] truncate ${
                    safeIndex === tab.key
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  title={tab.label}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              onClick={() => setMode('preview')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'preview'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Eye size={12} />
              Preview
            </button>
            <button
              onClick={() => setMode('code')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'code'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Code size={12} />
              DSL
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onOpenInStudio && hasDiagram && !isRegenerating && diagrams[safeIndex] && (
            <button
              type="button"
              disabled={openingStudio}
              onClick={async () => {
                setOpeningStudio(true)
                try {
                  await onOpenInStudio(diagrams[safeIndex])
                } finally {
                  setOpeningStudio(false)
                }
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/50 cursor-pointer disabled:opacity-60"
              title="Criar um projeto Studio com este diagrama, editável e com os agentes"
            >
              {openingStudio ? <Loader2 size={12} className="animate-spin" /> : <Shapes size={12} />}
              Abrir no Studio
            </button>
          )}
          {mode === 'preview' && hasDiagram && (
            <>
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 mr-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title="Zoom out"
                >
                  <ZoomOut size={12} />
                </button>
                <span className="text-[10px] font-mono text-gray-600 dark:text-gray-400 px-1 w-10 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title="Zoom in"
                >
                  <ZoomIn size={12} />
                </button>
                <button
                  onClick={handleReset}
                  className="p-1 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title="Reset view"
                >
                  <Move size={12} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {mode === 'preview' ? (
          <div
            ref={wrapperRef}
            className="flex-1 overflow-hidden bg-white dark:bg-gray-950 flex items-center justify-center"
            style={{ cursor: mode === 'preview' && hasDiagram ? 'grab' : 'default' }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {error ? (
              <div className="max-w-md text-center">
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 px-4 py-3 rounded-lg mb-3">
                  {error}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  O agente gerou código de diagrama inválido. Verifique a aba "DSL" para ver o raw text.
                </p>
              </div>
            ) : hasDiagram ? (
              <div
                ref={containerRef}
                className="mermaid"
                style={{
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                  transformOrigin: 'center center',
                  transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
                }}
              />
            ) : (
              <div className="text-center text-gray-400 dark:text-gray-600">
                <FileQuestion size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum diagrama gerado ainda.</p>
                <p className="text-xs mt-1">Marque pelo menos uma ideia como pronta para o time de agentes analisar.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
            <textarea
              value={dsl}
              onChange={(e) => setDsl(e.target.value)}
              spellCheck={false}
              placeholder="O diagrama gerado pelo agente aparecerá aqui..."
              className="w-full h-full px-3 py-2 text-xs font-mono bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-agent resize-none text-gray-800 dark:text-gray-200 custom-scrollbar placeholder:text-gray-400"
            />
          </div>
        )}

        <ArtifactStateOverlay
          isRegenerating={isRegenerating}
          hasContent={hasDiagram}
          label="Gerando novos diagramas"
          hint="Van Gogh escolhe quantos e quais diagramas representam o sistema."
        />
      </div>
    </div>
  )
}
